import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  ShoppingCartIcon,
  CreditCardIcon,
  TruckIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentTextIcon,
  GiftIcon,
  TagIcon,
  InformationCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { 
  CreditCardIcon as CreditCardIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  TruckIcon as TruckIconSolid
} from '@heroicons/react/24/solid';
import { selectCartItems, selectCartTotal, selectCartItemsCount, updateQuantity, removeFromCart, clearCart } from '../store/slices/cartSlice';
import { getImageUrl } from '../config/api';
import paymentService from '../services/paymentService';

const Checkout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const cartItemsCount = useSelector(selectCartItemsCount);
  const { user } = useSelector((state) => state.auth);

  // Form states
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    region: '',
    postalCode: '',
    additionalInfo: '',
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    orderNotes: ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  // Coupons disabled
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [estimatedDelivery, setEstimatedDelivery] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBankTransferModal, setBankTransferModal] = useState(false);
  const [bankAccountData, setBankAccountData] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);

  // Derive checkout items (buy-now preferred)
  const buyNowProduct = location.state?.product;
  const buyNowInitialQty = location.state?.quantity || 1;
  const [buyNowQty, setBuyNowQty] = useState(buyNowProduct ? Math.max(1, buyNowInitialQty) : 1);
  
  // Use useMemo to prevent infinite re-renders
  const checkoutItems = useMemo(() => {
    return buyNowProduct ? [{ product: buyNowProduct, quantity: buyNowQty }] : cartItems;
  }, [buyNowProduct, buyNowQty, cartItems]);
  
  const checkoutCount = useMemo(() => {
    return buyNowProduct ? buyNowQty : cartItemsCount;
  }, [buyNowProduct, buyNowQty, cartItemsCount]);

  // Redirect if nothing to checkout
  useEffect(() => {
    if (!buyNowProduct && checkoutItems.length === 0) {
      toast.error(t('checkout.emptyCart'));
      navigate('/products');
    }
  }, [checkoutItems, buyNowProduct, navigate]);

  // Calculate estimated delivery
  useEffect(() => {
    const today = new Date();
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + Math.floor(Math.random() * 3) + 2);
    setEstimatedDelivery(deliveryDate);
  }, []);

  // Stable pricing fetch function with duplicate-call guard
  const loadingRef = useRef(false);
  const lastPricingKeyRef = useRef(null);

  const fetchPricing = useCallback(async () => {
    if (checkoutItems.length === 0) return;

    // Build a stable signature to avoid duplicate calls for the same input
    const pricingKey = JSON.stringify({
      items: checkoutItems.map(i => ({ id: i.product._id, q: i.quantity })),
      coupon: appliedCoupon?.code || null,
    });

    if (lastPricingKeyRef.current === pricingKey) {
      return; // Already fetched for this exact input
    }

    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoadingPricing(true);
    try {
      const response = await paymentService.calculatePricing(checkoutItems, appliedCoupon?.code);
      setPricingData(response.data);
      lastPricingKeyRef.current = pricingKey;
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error(t('checkout.pricingError'));
    } finally {
      loadingRef.current = false;
      setLoadingPricing(false);
    }
  }, [checkoutItems, appliedCoupon?.code, t]);

  // Fetch pricing from backend
  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutItems, appliedCoupon?.code]);

  // Calculate totals from backend data or fallback to local calculation
  const subtotal = pricingData?.subtotal || (buyNowProduct ? (buyNowProduct.price * buyNowQty) : cartTotal);
  const shippingFee = 0; // Always free - platform covers shipping
  const discountAmount = pricingData?.discount || 0;
  const taxAmount = pricingData?.taxAmount || 0;
  const finalTotal = pricingData?.totalAmount || (subtotal + shippingFee + taxAmount - discountAmount);

  // Coupons disabled - no handler

  // Handle bank transfer receipt upload
  const handleReceiptUpload = async () => {
    if (!receiptFile || !bankAccountData?.orderId) {
      toast.error('يرجى اختيار ملف الإيصال أولاً');
      return;
    }

    setUploadingReceipt(true);
    try {
      await paymentService.uploadBankReceipt(bankAccountData.orderId, receiptFile);
      
      // Clear cart and navigate to success page
      if (!buyNowProduct) dispatch(clearCart());
      toast.success('تم رفع الإيصال بنجاح! سيتم مراجعته من قبل الإدارة');
      navigate(`/order-success/${bankAccountData.orderId}`);
    } catch (error) {
      console.error('Receipt upload error:', error);
      toast.error(error.message || 'فشل في رفع الإيصال');
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Handle order submission
  const handleSubmitOrder = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error(t('checkout.completeRequired'));
      return;
    }
    
    if (!formData.address || !formData.city) {
      toast.error(t('checkout.completeAddress'));
      return;
    }

    // لا نطلب بيانات البطاقة هنا لأن ميسر سيتكفل بجمعها بأمان داخل النموذج المضمن

    setIsProcessing(true);
    
    try {
      // Prepare order data
      const orderData = {
        items: checkoutItems.map(item => ({
          productId: item.product._id,
          quantity: item.quantity
        })),
        shippingAddress: {
          address: formData.address,
          city: formData.city,
          region: formData.region,
          postalCode: formData.postalCode,
          additionalInfo: formData.additionalInfo
        },
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        paymentMethod: formData.paymentMethod,
        orderNotes: formData.orderNotes,
        couponCode: appliedCoupon?.code
      };

      // Create payment
      const response = await paymentService.createPayment(orderData);

      if (formData.paymentMethod === 'card') {
        // Handle card payment with Moyasar
        const { order, payment } = response;
        
        // إظهار المودال أولاً للتأكد من وجود العنصر في DOM
        setShowPaymentModal(true);

        // انتظر دورة رندر لإضافة العنصر ثم فعّل ميسر
        setTimeout(async () => {
          // تفريغ الحاوية قبل التهيئة
          const container = document.querySelector('.mysr-form-subscription');
          if (container) container.innerHTML = '';

          await paymentService.initializeMoyasar('.mysr-form-subscription', {
          publishable_api_key: payment.publishable_api_key,
          amount: payment.amount,
          currency: payment.currency,
          description: payment.description,
          callback_url: `${window.location.origin}/order-success/${order._id}`,
          methods: ['creditcard'],
          metadata: payment.metadata || {},
          on_completed: async (paymentResult) => {
            // Payment completed successfully by Moyasar
            console.log('Moyasar payment completed:', paymentResult);
            
            // Don't clear cart here - wait for confirmation
            // Moyasar will redirect to callback_url automatically
            // If it doesn't, manually redirect with payment ID
            setTimeout(() => {
              window.location.href = `${window.location.origin}/order-success/${order._id}?payment_id=${paymentResult.id}`;
            }, 1000);
          },
          on_failure: (error) => {
            console.error('Payment failed:', error);
            toast.error('فشلت عملية الدفع. يرجى المحاولة مرة أخرى.');
            setIsProcessing(false);
          }
        });
        }, 0);
      } else if (formData.paymentMethod === 'bank') {
        // Handle bank transfer
        const { order, bankAccount } = response;
        setBankAccountData({ ...bankAccount, orderId: order._id, orderNumber: order.orderNumber, totalAmount: order.totalAmount });
        setBankTransferModal(true);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Order submission error:', error);
      toast.error(error.message || 'حدث خطأ أثناء معالجة طلبك');
      setIsProcessing(false);
    }
  };

  if (!buyNowProduct && checkoutItems.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-100/30 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-brand-blue/10 to-brand-blueSecondary/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-gradient-to-tr from-brand-green/8 to-brand-blue/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/4 w-64 h-64 bg-gradient-to-tl from-indigo-200/20 to-blue-200/10 rounded-full blur-2xl"></div>
      </div>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-brand-blue to-brand-blueSecondary rounded-lg">
                  <ShoppingCartIcon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />
                </div>
                {t('checkout.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {checkoutCount} {checkoutCount === 1 ? t('products.product') : t('products.products')} • {finalTotal.toLocaleString('ar-SA')} {t('products.sar')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Review */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary p-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <ShoppingCartIcon className="h-6 w-6" />
                  {t('checkout.orderSummary.title')}
                </h2>
                <p className="text-blue-100 mt-1">{t('checkout.reviewItems', 'تأكد من صحة المنتجات والكميات')}</p>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {checkoutItems.map((item, index) => (
                    <motion.div
                      key={item.product._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-xl hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 hover:shadow-md"
                    >
                      {/* Product Image */}
                      <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-white shadow-sm">
                        {item.product.images && item.product.images.length > 0 ? (
                          <img
                            src={getImageUrl(item.product.images[0].url)}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100">
                            <ShoppingCartIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{item.product.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-1">{item.product.description}</p>
                        <div className="flex items-center mt-2">
                          <span className="text-lg font-bold text-brand-blue">
                            {item.product.price.toLocaleString('ar-SA')} {t('products.sar')}
                          </span>
                          {/* إخفاء اسم المختبر البائع للحفاظ على الخصوصية */}
                        </div>
                      </div>

                      {/* Quantity Controls - disabled for buy-now flow */}
                      <div className="flex items-center gap-3">
                        {buyNowProduct ? (
                          <div className="flex items-center border border-gray-200 rounded-lg">
                            <button
                              onClick={() => setBuyNowQty(Math.max(1, item.quantity - 1))}
                              className="p-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
                              disabled={item.quantity <= 1}
                            >
                              <MinusIcon className="w-4 ه-4 text-gray-600" />
                            </button>
                            <span className="px-4 py-2 font-medium text-gray-900 min-w-[50px] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => setBuyNowQty(Math.min(item.product.quantity, item.quantity + 1))}
                              className="p-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
                              disabled={item.quantity >= item.product.quantity}
                            >
                              <PlusIcon className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center border border-gray-200 rounded-lg">
                              <button
                                onClick={() => dispatch(updateQuantity({ productId: item.product._id, quantity: Math.max(1, item.quantity - 1) }))}
                                className="p-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                disabled={item.quantity <= 1}
                              >
                                <MinusIcon className="w-4 h-4 text-gray-600" />
                              </button>
                              <span className="px-4 py-2 font-medium text-gray-900 min-w-[50px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => dispatch(updateQuantity({ productId: item.product._id, quantity: item.quantity + 1 }))}
                                className="p-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                disabled={item.quantity >= item.product.quantity}
                              >
                                <PlusIcon className="w-4 h-4 text-gray-600" />
                              </button>
                            </div>

                            <button
                              onClick={() => dispatch(removeFromCart(item.product._id))}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>

                      {/* Item Total */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {(item.product.price * item.quantity).toLocaleString('ar-SA')} ريال
                        </div>
                        <div className="text-sm text-gray-500">
                          {item.quantity} × {item.product.price.toLocaleString('ar-SA')}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Coupon Section removed */}
              </div>
            </motion.div>

            {/* Customer Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary p-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <UserIcon className="h-6 w-6" />
                  {t('checkout.personalInfo.title')}
                </h2>
                <p className="text-blue-100 mt-1">{t('checkout.personalInfoDesc', 'أدخل بياناتك للتواصل معك')}</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.personalInfo.firstName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="أحمد"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.personalInfo.lastName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="محمد"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.personalInfo.email')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="ahmed@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.personalInfo.phone')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <PhoneIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                        placeholder="05xxxxxxxx"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 mt-0.5 ml-2 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">{t('checkout.dataProtection', 'حماية بياناتك')}</p>
                      <p>{t('checkout.dataProtectionDesc', 'نحن نحمي بياناتك الشخصية ولن نشاركها مع أطراف ثالثة. سيتم استخدامها فقط لمعالجة طلبك والتواصل معك.')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Shipping Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary p-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <MapPinIcon className="h-6 w-6" />
                  {t('checkout.shipping.title')}
                </h2>
                <p className="text-blue-100 mt-1">{t('checkout.shippingDesc', 'حدد عنوان التوصيل المطلوب')}</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.shipping.address')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="الشارع، رقم المبنى، رقم الشقة..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.shipping.city')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      required
                    >
                      <option value="">{t('checkout.selectCity', 'اختر المدينة')}</option>
                      <option value="riyadh">الرياض</option>
                      <option value="jeddah">جدة</option>
                      <option value="dammam">الدمام</option>
                      <option value="mecca">مكة المكرمة</option>
                      <option value="medina">المدينة المنورة</option>
                      <option value="khobar">الخبر</option>
                      <option value="taif">الطائف</option>
                      <option value="tabuk">تبوك</option>
                      <option value="abha">أبها</option>
                      <option value="hail">حائل</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('checkout.shipping.postalCode')}
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      placeholder="12345"
                    />
                  </div>
                </div>

                {/* Shipping Info */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <TruckIconSolid className="w-6 h-6 text-blue-600 ml-2" />
                    <h3 className="font-semibold text-blue-900">{t('checkout.shippingInfo', 'معلومات التوصيل')}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <ClockIcon className="w-4 h-4 text-blue-600 ml-2" />
                      <span className="text-sm text-blue-800">
                        {t('checkout.deliveryDuration', 'مدة التوصيل: 2-5 أيام عمل')}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <ShieldCheckIconSolid className="w-4 h-4 text-blue-600 ml-2" />
                      <span className="text-sm text-blue-800">
                        {t('checkout.freeShipping', 'شحن مجاني ومضمون')}
                      </span>
                    </div>
                  </div>
                  {estimatedDelivery && (
                    <div className="mt-3 p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>{t('checkout.estimatedDelivery')}:</strong>{' '}
                        {estimatedDelivery.toLocaleDateString('ar-SA', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Payment Method */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary p-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-3">
                  <CreditCardIcon className="h-6 w-6" />
                  {t('checkout.payment.title')}
                </h2>
                <p className="text-blue-100 mt-1">{t('checkout.paymentDesc', 'اختر طريقة الدفع المناسبة')}</p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="relative">
                    <input
                      type="radio"
                      id="card"
                      name="paymentMethod"
                      value="card"
                      checked={formData.paymentMethod === 'card'}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      className="sr-only"
                    />
                    <label
                      htmlFor="card"
                      className={`relative flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                        formData.paymentMethod === 'card'
                          ? 'border-brand-blue bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-brand-blue to-brand-green text-white text-xs font-bold px-2 py-1 rounded-full">
                        آمن وسريع
                      </div>
                      <CreditCardIconSolid className={`w-8 h-8 mb-2 ${
                        formData.paymentMethod === 'card' ? 'text-brand-blue' : 'text-gray-400'
                      }`} />
                      <span className={`font-medium text-sm text-center ${
                        formData.paymentMethod === 'card' ? 'text-brand-blue' : 'text-gray-700'
                      }`}>
                        {t('checkout.payment.card')}
                      </span>
                      <span className="text-xs text-gray-500 text-center mt-1">
                        {t('checkout.visaMaster', 'فيزا أو ماستركارد')}
                      </span>
                      {formData.paymentMethod === 'card' && (
                        <CheckCircleIconSolid className="absolute top-2 left-2 w-5 h-5 text-brand-blue" />
                      )}
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      id="bank"
                      name="paymentMethod"
                      value="bank"
                      checked={formData.paymentMethod === 'bank'}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                      className="sr-only"
                    />
                    <label
                      htmlFor="bank"
                      className={`relative flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                        formData.paymentMethod === 'bank'
                          ? 'border-brand-blue bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-brand-blue to-brand-green text-white text-xs font-bold px-2 py-1 rounded-full">
                        الأكثر استخداماً
                      </div>
                      <DocumentTextIcon className={`w-8 h-8 mb-2 ${
                        formData.paymentMethod === 'bank' ? 'text-brand-blue' : 'text-gray-400'
                      }`} />
                      <span className={`font-medium text-sm text-center ${
                        formData.paymentMethod === 'bank' ? 'text-brand-blue' : 'text-gray-700'
                      }`}>
                        {t('checkout.payment.bank', 'تحويل بنكي')}
                      </span>
                      <span className="text-xs text-gray-500 text-center mt-1">
                        {t('checkout.payment.bankDesc', 'تحويل مباشر من البنك')}
                      </span>
                      {formData.paymentMethod === 'bank' && (
                        <CheckCircleIconSolid className="absolute top-2 left-2 w-5 h-5 text-brand-blue" />
                      )}
                    </label>
                  </div>
                </div>

                {/* Security Notice */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start">
                    <LockClosedIcon className="w-5 h-5 text-green-600 mt-0.5 icon-text flex-shrink-0" />
                    <div className="text-sm text-green-800">
                      <p className="font-medium mb-1">{t('checkout.secureTransaction', 'معاملة آمنة ومحمية')}</p>
                      <p>{t('checkout.sslProtection', 'جميع المعلومات محمية بتشفير SSL وتتم معالجة المدفوعات عبر بوابات دفع آمنة معتمدة.')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Order Button */}
            <div className="flex items-center justify-center">
              <motion.button
                onClick={handleSubmitOrder}
                disabled={isProcessing}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative px-12 py-4 bg-gradient-to-r from-brand-blue via-brand-blueSecondary to-brand-blue text-white rounded-xl hover:shadow-2xl transition-all duration-300 font-bold text-lg shadow-lg disabled:opacity-50 flex items-center overflow-hidden group"
              >
                {/* Background Animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Content */}
                <div className="relative flex items-center">
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent ml-3"></div>
                      {t('checkout.processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-6 h-6 icon-text" />
                      {t('checkout.placeOrder')}
                    </>
                  )}
                </div>
                
                {/* Shine Effect */}
                <div className="absolute inset-0 -top-2 -bottom-2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </motion.button>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6" />
                    {t('checkout.orderSummary.title')}
                  </h3>
                  <p className="text-gray-300 mt-1">{checkoutCount} {checkoutCount === 1 ? t('products.product') : t('products.products')}</p>
                </div>

                <div className="p-6">
                  {/* Order Items Summary */}
                  <div className="space-y-3 mb-6">
                    {checkoutItems.slice(0, 3).map((item) => (
                      <div key={item.product._id} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {item.product.images && item.product.images.length > 0 ? (
                            <img
                              src={getImageUrl(item.product.images[0].url)}
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ShoppingCartIcon className="w-6 h-6 text-gray-400 m-3" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.quantity} × {item.product.price.toLocaleString('ar-SA')} {t('products.sar')}
                          </p>
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {(item.product.price * item.quantity).toLocaleString('ar-SA')} ريال
                        </div>
                      </div>
                    ))}
                    {checkoutItems.length > 3 && (
                      <div className="text-center text-sm text-gray-500 py-2">
                        {t('checkout.moreItems', 'و {{count}} منتج آخر...', { count: checkoutItems.length - 3 })}
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  <div className="border-t border-gray-200 pt-4 space-y-3">
                    {loadingPricing && (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-blue border-t-transparent ml-2"></div>
                        <span className="text-sm text-gray-600">{t('checkout.calculatingPrices', 'جاري حساب الأسعار...')}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{t('checkout.orderSummary.subtotal')}:</span>
                      <span className="font-medium">{subtotal.toLocaleString('ar-SA')} {t('products.sar')}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center">
                        {t('checkout.orderSummary.shipping')}:
                        <span className="mr-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {t('checkout.free')}
                        </span>
                      </span>
                      <span className="font-medium text-green-600">
                        {t('checkout.free')} 🚚
                      </span>
                    </div>

                    {appliedCoupon && discountAmount > 0 && (
                      <div className="flex items-center justify-between text-green-600">
                        <span className="flex items-center">
                          <TagIcon className="w-4 h-4 icon-text" />
                          {t('checkout.orderSummary.discount')} ({appliedCoupon.code}):
                        </span>
                        <span className="font-medium">
                          -{discountAmount.toLocaleString('ar-SA')} {t('products.sar')}
                        </span>
                      </div>
                    )}

                    {taxAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">{t('checkout.orderSummary.tax')} (15%):</span>
                        <span className="font-medium">{taxAmount.toLocaleString('ar-SA')} {t('products.sar')}</span>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex items-center justify-between text-lg font-bold">
                        <span>{t('checkout.orderSummary.total')}:</span>
                        <span className="text-brand-blue">
                          {finalTotal.toLocaleString('ar-SA')} {t('products.sar')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Free Shipping Message */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center">
                      <TruckIcon className="w-5 h-5 text-blue-600 icon-text" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          🎉 {t('checkout.totallyFree', 'الشحن مجاني تماماً!')}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {t('checkout.platformCovers', 'منصة بديل تتكفل بجميع رسوم الشحن والتوصيل')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="flex items-center p-3 bg-green-50 rounded-lg">
                      <ShieldCheckIconSolid className="w-5 h-5 text-green-600 icon-text" />
                      <span className="text-xs text-green-800 font-medium">{t('checkout.securePayment')}</span>
                    </div>
                    <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                      <TruckIconSolid className="w-5 h-5 text-blue-600 icon-text" />
                      <span className="text-xs text-blue-800 font-medium">{t('checkout.freeShipping')}</span>
                    </div>
                  </div>

                  {/* Platform Info */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center mb-2">
                      <InformationCircleIcon className="w-5 h-5 text-indigo-600 icon-text" />
                      <span className="text-sm font-medium text-indigo-800">{t('checkout.platformServices', 'خدمات المنصة')}</span>
                    </div>
                    <div className="space-y-1 text-xs text-indigo-700">
                      <p>• {t('checkout.freeShippingCompletely', 'الشحن مجاني تماماً')} 🚚</p>
                      <p>• {t('checkout.pricesIncludeTax', 'جميع الأسعار شاملة ضريبة القيمة المضافة')}</p>
                      <p>• {t('checkout.securePayment100', 'دفع آمن ومحمي 100%')}</p>
                    </div>
                  </div>


                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Modal */}
      <AnimatePresence>
        {isProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full text-center"
            >
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-blue border-t-transparent mx-auto mb-6"></div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">جاري معالجة طلبك</h3>
              <p className="text-gray-600 mb-4">يرجى عدم إغلاق الصفحة أو الضغط على زر الرجوع</p>
              <div className="flex items-center justify-center space-x-2 space-x-reverse">
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Moyasar Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  إتمام عملية الدفع
                </h3>
                <p className="text-gray-600">
                  أدخل بيانات البطاقة الائتمانية لإتمام الطلب
                </p>
              </div>
              
              <div className="mysr-form-subscription mb-6"></div>
              
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setIsProcessing(false);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bank Transfer Modal */}
      <AnimatePresence>
        {showBankTransferModal && bankAccountData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('checkout.bankTransfer.title', 'بيانات التحويل البنكي')}
                </h3>
                <p className="text-gray-600">
                  {t('checkout.bankTransfer.subtitle', 'قم بالتحويل إلى الحساب التالي ثم ارفع إيصال التحويل')}
                </p>
              </div>

              {/* Bank Account Details */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-200">
                <h4 className="font-bold text-blue-900 mb-4 text-center">
                  {t('checkout.bankTransfer.accountDetails', 'بيانات الحساب البنكي')}
                </h4>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">{t('checkout.bankTransfer.iban', 'رقم الآيبان')}:</span>
                    <span className="font-mono font-bold text-lg text-blue-900">{bankAccountData.iban}</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">{t('checkout.bankTransfer.accountName', 'اسم الحساب')}:</span>
                    <span className="font-bold text-blue-900">
                      {t('locale') === 'ar' ? bankAccountData.nameAr : bankAccountData.nameEn}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-600">{t('checkout.bankTransfer.bankName', 'اسم البنك')}:</span>
                    <span className="font-bold text-blue-900">
                      {t('locale') === 'ar' ? bankAccountData.bankName : bankAccountData.bankNameEn}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-gray-600">{t('checkout.bankTransfer.amount', 'المبلغ المطلوب')}:</span>
                    <span className="font-bold text-xl text-green-600">
                      {bankAccountData.totalAmount?.toLocaleString('ar-SA')} {t('products.sar')}
                    </span>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>{t('checkout.bankTransfer.important', 'مهم')}:</strong> {t('checkout.bankTransfer.exactAmount', 'يرجى التأكد من تحويل المبلغ الصحيح تماماً')}
                  </p>
                </div>
              </div>

              {/* Order Details */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h5 className="font-semibold text-gray-900 mb-2">{t('checkout.orderDetails', 'تفاصيل الطلب')}:</h5>
                <p><strong>{t('checkout.orderNumber', 'رقم الطلب')}:</strong> {bankAccountData.orderNumber}</p>
                <p><strong>{t('checkout.totalAmount', 'المبلغ الإجمالي')}:</strong> {bankAccountData.totalAmount?.toLocaleString('ar-SA')} {t('products.sar')}</p>
              </div>

              {/* Receipt Upload */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">
                  {t('checkout.bankTransfer.uploadReceipt', 'رفع إيصال التحويل')}
                </h4>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  <input
                    type="file"
                    id="receipt-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="hidden"
                  />
                  <label
                    htmlFor="receipt-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <DocumentTextIcon className="w-12 h-12 text-gray-400 mb-2" />
                    <span className="text-gray-600 mb-1">
                      {receiptFile ? receiptFile.name : t('checkout.bankTransfer.selectFile', 'اختر ملف الإيصال')}
                    </span>
                    <span className="text-sm text-gray-500">
                      {t('checkout.bankTransfer.supportedFormats', 'PDF, JPG, PNG')}
                    </span>
                  </label>
                </div>
                
                {receiptFile && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                      ✅ {t('checkout.bankTransfer.fileSelected', 'تم اختيار الملف')}: {receiptFile.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setBankTransferModal(false);
                    setBankAccountData(null);
                    setReceiptFile(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('checkout.cancel', 'إلغاء')}
                </button>
                
                <button
                  onClick={handleReceiptUpload}
                  disabled={!receiptFile || uploadingReceipt}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingReceipt ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent ml-2"></div>
                      {t('checkout.bankTransfer.uploading', 'جاري الرفع...')}
                    </div>
                  ) : (
                    t('checkout.bankTransfer.confirmTransfer', 'تأكيد التحويل')
                  )}
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>{t('checkout.bankTransfer.note', 'ملاحظة')}:</strong> {t('checkout.bankTransfer.reviewTime', 'سيتم مراجعة الإيصال خلال 24 ساعة وإعلامك بالنتيجة')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Checkout;
